import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MapPin, Navigation, Clock, User, Phone, Package, Eye, Filter, X, Route } from 'lucide-react';
import { useAppSelector } from '@/redux/hooks';
import { toast } from 'sonner';
import OrderTrackingModal from '@/components/OrderTrackingModal';
import { pharmacyTrackingService } from '@/api/trackingService';
import { getAccessToken } from '@/api/tokenService';
import GlobalLoader from "../../components/ui/GlobalLoader";

interface Route {
  _id: string;
  routeId: string;
  name: string;
  description: string;
  waypoints: Array<{
    _id?: string;
    lat: number;
    lng: number;
    address: string;
    order: number;
    type: 'pharmacy' | 'delivery' | 'other';
    pharmacy?: string;
    routeType?: string;
    completed?: boolean;
  }>;
  assignedDriver: string;
  estimatedTime: number;
  distance: number;
  status: string;
  routeType: string;
  orders: string[];
  pickupOrder: string[];
  completedWaypoints: string[];
  trackingData?: {
    latitude: number;
    longitude: number;
    speed: number;
    heading: number;
    lastUpdated: string;
    isOnline: boolean;
  };
  isRiderOnRoute?: boolean;
  allOrdersCompleted?: boolean;
  riderInfo?: {
    id: string;
    name: string;
    phone: string;
    vehicleType: string;
    vehicleNumber: string;
    availability: string;
  };
}

interface RouteTrackingPageProps {}

const RouteTrackingPage: React.FC<RouteTrackingPageProps> = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedRoute, setSelectedRoute] = useState<Route | null>(null);

  console.log('🔍 RouteTrackingPage - User:', user);
  console.log('🔍 RouteTrackingPage - User ID:', user?._id);
  console.log('🔍 RouteTrackingPage - User Role:', user?.role);
  console.log('🔍 RouteTrackingPage - Token:', getAccessToken());

  const fetchRoutes = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching pharmacy routes...');
      const response = await pharmacyTrackingService.getPharmacyRoutesWithTracking({
        search: searchTerm,
        page: currentPage,
        limit: 20
      });

      console.log('📊 API Response:', response);

      if (response.success) {
        console.log('✅ Routes fetched successfully:', response.data.routes.length);
        setRoutes(response.data.routes);
        setTotalPages(response.data.pagination.total);
        setTotalCount(response.data.pagination.totalCount);
      } else {
        console.error('❌ API Error:', response.message);
        toast.error(response.message || 'Failed to fetch routes');
      }
    } catch (error) {
      console.error('❌ Error fetching routes:', error);
      toast.error('Failed to fetch routes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoutes();
  }, [currentPage, searchTerm]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleTrackRoute = (route: Route) => {
    if (route.allOrdersCompleted) {
      toast.info('This route is completed. Viewing route details.');
    }
    setSelectedRoute(route);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800';
      case 'assigned':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getButtonText = (route: Route) => {
    if (route.allOrdersCompleted) {
      return 'View Route';
    }
    return 'Track Live';
  };

  const getButtonVariant = (route: Route) => {
    if (route.allOrdersCompleted) {
      return 'outline';
    }
    return 'default';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <GlobalLoader isLoading={true} message="Processing please wait..." subMessage="Please wait" />
          <p className="mt-2 text-gray-600">Loading routes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Live Tracking</h1>
          <p className="text-gray-600">Track your pharmacy routes and drivers in real-time</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search routes..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Routes List */}
      {routes.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Package className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Routes Found</h3>
            <p className="text-gray-600 text-center">
              No routes available for tracking at the moment.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {routes.map((route) => (
            <Card key={route._id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <Route className="h-5 w-5 text-blue-600" />
                      <h3 className="text-lg font-semibold text-gray-900">{route.name}</h3>
                      <Badge className={getStatusColor(route.status)}>
                        {route.status}
                      </Badge>
                      {route.allOrdersCompleted && (
                        <Badge variant="outline" className="bg-gray-100 text-gray-600">
                          Completed
                        </Badge>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-600">Driver:</span>
                        <span className="text-sm font-medium">{route.riderInfo?.name || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-600">Phone:</span>
                        <span className="text-sm font-medium">{route.riderInfo?.phone || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Navigation className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-600">Vehicle:</span>
                        <span className="text-sm font-medium">{route.riderInfo?.vehicleType || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-600">ETA:</span>
                        <span className="text-sm font-medium">{Math.round(route.estimatedTime)} min</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-600">Distance:</span>
                        <span className="text-sm font-medium">{route.distance.toFixed(1)} km</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-600">Orders:</span>
                        <span className="text-sm font-medium">{route.orders.length + route.pickupOrder.length}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${route.trackingData?.isOnline ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <span className="text-sm text-gray-600">Status:</span>
                        <span className={`text-sm font-medium ${route.trackingData?.isOnline ? 'text-green-600' : 'text-red-600'}`}>
                          {route.trackingData?.isOnline ? 'Online' : 'Offline'}
                        </span>
                      </div>
                    </div>

                    {route.trackingData && (
                      <div className="text-xs text-gray-500 mb-4">
                        Last updated: {new Date(route.trackingData.lastUpdated).toLocaleString()}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={() => handleTrackRoute(route)}
                      variant={getButtonVariant(route)}
                      className="flex items-center gap-2"
                      disabled={!route.trackingData?.isOnline && !route.allOrdersCompleted}
                    >
                      <Eye className="h-4 w-4" />
                      {getButtonText(route)}
                    </Button>
                    
                    {!route.trackingData?.isOnline && !route.allOrdersCompleted && (
                      <span className="text-xs text-gray-500 text-center">
                        Rider Offline
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Page {currentPage} of {totalPages} ({totalCount} total routes)
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Tracking Modal */}
      {selectedRoute && (
        <OrderTrackingModal
          isOpen={!!selectedRoute}
          onClose={() => setSelectedRoute(null)}
          routeData={selectedRoute}
          isRouteTracking={true}
        />
      )}
    </div>
  );
};

export default RouteTrackingPage;
